import { useState, useEffect, useCallback, useRef } from 'react';

interface ExchangeRate {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  rate: number;
  validUntil: number;
  isLoading: boolean;
  error: string | null;
}

interface UseExchangeRateOptions {
  refreshInterval?: number; // in milliseconds, default 10000 (10 seconds)
  autoRefresh?: boolean; // default true
  minRefreshInterval?: number; // minimum interval between API calls to prevent abuse
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ChangeNOWResponse {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string | number;
  toAmount: string | number;
  rate: string | number;
  validUntil?: number;
  fromAPI?: boolean;
  responseTime?: number;
  transactionSpeed?: string;
  warning?: string | null;
}

// Use direct backend URL in development, proxy in production
const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

// Helper function to normalize currency names for API calls
const normalizeCurrencyForAPI = (currency: string): { ticker: string; network?: string } => {
  const lowerCurrency = currency.toLowerCase();
  
  // Check if currency includes network (e.g., "usdt_eth", "usdt_trx")
  if (lowerCurrency.includes('_')) {
    const [ticker, network] = lowerCurrency.split('_');
    return { ticker, network };
  }
  
  // Otherwise return just the ticker
  return { ticker: lowerCurrency };
};

export const useExchangeRate = (
  initialFromCurrency: string = '',
  initialToCurrency: string = '',
  initialAmount: number = 0,
  options: UseExchangeRateOptions = {}
) => {
  const {
    refreshInterval = 10000, // 10 seconds default
    autoRefresh = true,
    minRefreshInterval = 2000 // Minimum 2 seconds between API calls
  } = options;

  // Initialize with safe defaults to prevent initialization errors
  const [exchangeRate, setExchangeRate] = useState<ExchangeRate>({
    fromCurrency: initialFromCurrency || 'btc',
    toCurrency: initialToCurrency || 'eth',
    fromAmount: initialAmount || 0,
    toAmount: 0,
    rate: 0,
    validUntil: Date.now(),
    isLoading: false,
    error: null
  });

  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(autoRefresh);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<number>(0);
  const activeRequestRef = useRef<AbortController | null>(null);

  const fetchExchangeRate = useCallback(async (
    fromCurrency: string,
    toCurrency: string,
    amount: number,
    skipCache: boolean = false
  ): Promise<void> => {
    console.log('🔄 [FRONTEND] fetchExchangeRate called:', {
      fromCurrency,
      toCurrency,
      amount,
      skipCache
    });

    if (!fromCurrency || !toCurrency || amount <= 0) {
      setExchangeRate(prev => ({
        ...prev,
        toAmount: 0,
        rate: 0,
        isLoading: false,
        error: 'Please select valid currencies and amount'
      }));
      return;
    }

    // Prevent too frequent API calls
    const now = Date.now();
    if (!skipCache && now - lastFetchRef.current < minRefreshInterval) {
      console.log(`Skipping API call - too frequent (${now - lastFetchRef.current}ms ago)`);
      return;
    }

    // Cancel any existing request
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    activeRequestRef.current = new AbortController();
    lastFetchRef.current = now;

    setExchangeRate(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // Validate inputs before making request
      if (!fromCurrency || !toCurrency) {
        throw new Error('Invalid currencies provided');
      }

      // First try to backend API with properly normalized currency names
      const fromAPI = normalizeCurrencyForAPI(fromCurrency);
      const toAPI = normalizeCurrencyForAPI(toCurrency);

      const requestBody: any = {
        fromCurrency: fromAPI.ticker,
        toCurrency: toAPI.ticker,
        fromAmount: amount,
      };

      // Add network parameters if available
      if (fromAPI.network) {
        requestBody.fromNetwork = fromAPI.network;
      }
      if (toAPI.network) {
        requestBody.toNetwork = toAPI.network;
      }

      console.log('🔍 [FRONTEND] Sending API request:', requestBody);

      const response = await fetch(`${API_URL}/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: activeRequestRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const responseJson = await response.json();

      // Validate response structure
      if (!responseJson || typeof responseJson !== 'object') {
        throw new Error('Invalid API response format');
      }

      // Check if response is successful
      if (!responseJson.success) {
        throw new Error(responseJson.error || 'API returned unsuccessful response');
      }

      const data = responseJson.data;

      // Validate data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid API data format');
      }

      console.log('✅ [FRONTEND] API Response received:', {
        fromCurrency,
        toCurrency,
        fromAmount: amount,
        toAmount: data.toAmount,
        rate: data.rate,
        fromAPI: data.fromAPI,
        responseTime: data.responseTime,
        fullData: data
      });

      setExchangeRate(prev => ({
        ...prev,
        fromCurrency,
        toCurrency,
        fromAmount: amount,
        toAmount: parseFloat(String(data.toAmount)) || 0,
        rate: parseFloat(String(data.rate)) || 0,
        validUntil: Date.now() + 30000,
        isLoading: false,
        error: null
      } as ExchangeRate));

      console.log('💰 [FRONTEND] Rate updated in state:', {
        fromCurrency,
        toCurrency,
        fromAmount: amount,
        toAmount: data.toAmount,
        rate: data.rate
      });

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request aborted due to new request');
        return;
      }

      console.warn('Backend API failed:', error);

      // Set error state when API fails
      setExchangeRate(prev => ({
        ...prev,
        fromCurrency,
        toCurrency,
        fromAmount: amount,
        toAmount: 0,
        rate: 0,
        validUntil: Date.now() + 60000,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch exchange rate'
      }));
    }
  }, [minRefreshInterval]);

  // Simple debounce function
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedFetchRate = useCallback((
    fromCurrency: string,
    toCurrency: string,
    amount: number
  ) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchExchangeRate(fromCurrency, toCurrency, amount);
    }, 200); // Reduced from 500ms to 200ms for faster response
  }, [fetchExchangeRate]);

  // Update rates when currencies or amount change
  const updateExchange = useCallback((
    fromCurrency: string,
    toCurrency: string,
    amount: number,
    immediate: boolean = false
  ) => {
    if (immediate) {
      fetchExchangeRate(fromCurrency, toCurrency, amount, true);
    } else {
      debouncedFetchRate(fromCurrency, toCurrency, amount);
    }
  }, [fetchExchangeRate, debouncedFetchRate]);

  // Trigger immediate refresh
  const refreshRates = useCallback(() => {
    if (exchangeRate.fromCurrency && exchangeRate.toCurrency && exchangeRate.fromAmount > 0) {
      fetchExchangeRate(
        exchangeRate.fromCurrency,
        exchangeRate.toCurrency,
        exchangeRate.fromAmount,
        true
      );
    }
  }, [exchangeRate, fetchExchangeRate]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    setIsAutoRefreshEnabled(enabled);
  }, []);

  // Setup auto-refresh interval
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isAutoRefreshEnabled &&
        exchangeRate.fromCurrency &&
        exchangeRate.toCurrency &&
        exchangeRate.fromAmount > 0) {

      intervalRef.current = setInterval(() => {
        refreshRates();
      }, refreshInterval);

      console.log(`Auto-refresh enabled: ${refreshInterval}ms interval`);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    isAutoRefreshEnabled,
    exchangeRate.fromCurrency,
    exchangeRate.toCurrency,
    exchangeRate.fromAmount,
    refreshInterval,
    refreshRates
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    };
  }, []);

  // Log rate updates for debugging
  useEffect(() => {
    if (exchangeRate.rate > 0) {
      console.log(`Rate updated: 1 ${exchangeRate.fromCurrency} = ${exchangeRate.rate} ${exchangeRate.toCurrency}`);
      console.log(`Valid until: ${new Date(exchangeRate.validUntil).toLocaleTimeString()}`);
    }
  }, [exchangeRate.rate, exchangeRate.fromCurrency, exchangeRate.toCurrency, exchangeRate.validUntil]);

  return {
    exchangeRate,
    updateExchange,
    refreshRates,
    toggleAutoRefresh,
    isAutoRefreshEnabled
  };
};